/**
 * מבט משרד — workload per bookkeeper, engagement, and the exception panel.
 *
 * The workload numbers are read live rather than from a rollup, deliberately: the question
 * this screen answers is "who is drowning right now", and a yesterday-shaped answer is
 * useless for that. Everything else here comes from precomputed rollups, because it is
 * aggregation across four modules.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatDate, formatMonthName } from "@onecpa/shared";
import {
  Badge,
  type Column,
  DataTable,
  EmptyState,
  ErrorState,
  KpiStrip,
  Money,
  PageHeader,
  Panel,
  StatusBadge,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { agorot, minutesToLabel, usePeriod, useRoleGate } from "../app/hooks.js";
import { useSession } from "../app/session.js";

interface WorkloadRow {
  user_id: string;
  full_name: string;
  client_count: number;
  open_tasks: number;
  minutes_this_month: number;
  blocked_cases: number;
}

export function OfficeControl() {
  const session = useSession();
  const { allowed, role } = useRoleGate(["owner", "manager"]);
  const { period, label } = usePeriod();

  const workload = useQuery({
    queryKey: ["workload", session.orgId],
    queryFn: () => api.analytics.workload(session.orgId),
    enabled: allowed,
  });

  const engagement = useQuery({
    queryKey: ["engagement", session.orgId, period.year, period.month],
    queryFn: () => api.analytics.engagement(session.orgId, period),
    enabled: allowed,
  });

  const exceptions = useQuery({
    queryKey: ["reporting", "exceptions", session.orgId],
    queryFn: () => api.reporting.exceptions(session.orgId),
    enabled: allowed,
  });

  const dashboard = useQuery({
    queryKey: ["dashboard", session.orgId],
    queryFn: () => api.analytics.dashboard(session.orgId),
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <ErrorState
        title="אין הרשאה למסך זה"
        message={`מבט משרד זמין לבעל המשרד ולמנהל המשרד. התפקיד שלך: ${role}.`}
      />
    );
  }

  if (workload.error) {
    return (
      <ErrorState
        message={(workload.error as Error).message}
        onRetry={() => void workload.refetch()}
      />
    );
  }

  const columns: Column<WorkloadRow>[] = [
    {
      key: "name",
      header: "עובד",
      sticky: true,
      width: "180px",
      cell: (row) => <span className="text-sm font-semibold text-ink">{row.full_name}</span>,
    },
    {
      key: "clients",
      header: "לקוחות",
      align: "center",
      cell: (row) => <bdi className="tnum text-xs text-ink">{row.client_count}</bdi>,
    },
    {
      key: "tasks",
      header: "משימות פתוחות",
      align: "center",
      cell: (row) => (
        <bdi
          className={`tnum text-xs ${row.open_tasks > 10 ? "font-semibold text-ochre" : "text-ink"}`}
        >
          {row.open_tasks}
        </bdi>
      ),
    },
    {
      key: "hours",
      header: "שעות החודש",
      align: "center",
      cell: (row) => (
        <bdi className="tnum text-xs text-ink-muted">
          {minutesToLabel(row.minutes_this_month)}
        </bdi>
      ),
    },
    {
      key: "blocked",
      header: "תיקים תקועים",
      align: "center",
      cell: (row) =>
        row.blocked_cases > 0 ? (
          <Badge tone="warning">{row.blocked_cases}</Badge>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        ),
    },
  ];

  const money = dashboard.data?.money;

  return (
    <>
      <PageHeader title="מבט משרד" subtitle={session.orgName} period={label} />

      <KpiStrip
        items={[
          // No `?? 0` on any value: undefined means "still loading" and draws a
          // skeleton, where a zero would claim there is nothing to report. The
          // tones still coalesce, because a comparison needs a number.
          { label: "דיווחים החודש", value: dashboard.data?.reporting.total },
          {
            label: "בסיכון לקנס",
            value: dashboard.data?.reporting.at_risk,
            tone: (dashboard.data?.reporting.at_risk ?? 0) > 0 ? "red" : "ink",
          },
          {
            label: "תיקים ממתינים ללקוח",
            value: dashboard.data?.cases.blocked,
            tone: "ochre",
          },
          {
            label: "משימות באיחור",
            value: dashboard.data?.tasks.overdue,
            tone: (dashboard.data?.tasks.overdue ?? 0) > 0 ? "ochre" : "ink",
          },
          money
            ? {
                label: "חוב פתוח",
                value: <Money agorot={agorot(money.outstanding)} colorize={false} />,
                tone: "ochre" as const,
              }
            : {
                label: "שעות החודש",
                value: dashboard.data ? minutesToLabel(dashboard.data.minutesThisMonth) : undefined,
              },
        ]}
      />

      <div className="grid gap-4 p-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <DataTable
            columns={columns}
            rows={workload.data?.workload ?? []}
            rowKey={(row) => row.user_id}
            loading={workload.isLoading}
            caption="עומס עבודה לפי עובד"
            footnote="שעות נמדדות מרשומות שנסגרו בחודש הנוכחי · «תיקים תקועים» = תיקים שממתינים להשלמה מהלקוח"
            empty={<EmptyState title="אין עובדים" />}
          />
        </div>

        <Panel title="חריגות דיווח">
          {(exceptions.data?.exceptions ?? []).length > 0 ? (
            <ul className="divide-y divide-line-soft">
              {(exceptions.data?.exceptions ?? []).slice(0, 10).map((row) => (
                <li key={row.id} className="px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <Link
                      to="/clients/$clientId"
                      params={{ clientId: row.clientId }}
                      className="link-underline min-w-0 flex-1 truncate text-xs font-semibold text-ink"
                    >
                      {row.clientName}
                    </Link>
                    <StatusBadge vocabulary="monthly" status={row.status} />
                  </div>
                  <p className="mt-0.5 text-2xs text-ink-faint">
                    {formatMonthName(row.periodMonth)} {row.periodYear} · מועד{" "}
                    <bdi className="tnum">{formatDate(row.dueDate)}</bdi>
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="אין חריגות" icon="check" />
          )}
        </Panel>

        <Panel title="שיתוף פעולה של לקוחות" className="lg:col-span-3">
          {(engagement.data?.rows ?? []).length > 0 ? (
            <ul className="divide-y divide-line-soft">
              {(engagement.data?.rows ?? []).slice(0, 12).map((row) => (
                <li key={row.clientId} className="flex flex-wrap items-center gap-3 px-3.5 py-2">
                  <Link
                    to="/clients/$clientId"
                    params={{ clientId: row.clientId }}
                    className="link-underline min-w-40 flex-1 truncate text-xs font-semibold text-ink"
                  >
                    {row.clientName}
                  </Link>
                  <span className="tnum w-10 text-xs text-ink">{row.score}</span>
                  <span
                    className="h-1.5 w-24 overflow-hidden rounded-[2px] bg-wash"
                    role="progressbar"
                    aria-valuenow={row.score}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`ציון שיתוף פעולה של ${row.clientName}`}
                  >
                    <span
                      className={`block h-full ${row.score >= 65 ? "bg-ledger" : row.score >= 40 ? "bg-ochre" : "bg-red-ink"}`}
                      style={{ width: `${row.score}%` }}
                    />
                  </span>
                  <span className="tnum text-2xs text-ink-faint">
                    {row.onTimeCount} בזמן · {row.lateCount} באיחור · {row.remindersSent} תזכורות
                  </span>
                  <StatusBadge vocabulary="engagement" status={row.level} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="אין נתוני שיתוף פעולה לחודש זה" />
          )}
          <p className="border-t border-line-soft px-3.5 py-2 text-2xs text-ink-faint">
            הציון נבנה מדיוק בזמנים, ממספר התזכורות שנדרשו ומזמן התגובה — ולא מגובה ההכנסה מהלקוח.
          </p>
        </Panel>
      </div>
    </>
  );
}
